/**
 * Conversions entre les lignes de la base (snake_case, dates en texte) et les objets de
 * l'application. Les noms de colonnes sont ceux de supabase/migrations.
 */
import type { CarteMere, TypeCarte } from '../cartes/carte-mere';
import { reportAuLendemain } from '../cartes/carte-mere';
import type { Touches } from '../clavier/touches';
import { State, type EtatFsrs, type Note } from '../fsrs/planificateur';
import type {
  AffichageMinuteur,
  Etapes,
  OrdreCartes,
  ReglagesPaquet,
} from '../paquets/reglages';
import type { CarteDuPaquet, RevisionPassee } from '../seance/composition';

// --- Paquets ---------------------------------------------------------------------------

export interface LignePaquet {
  id: string;
  parent_id: string | null;
  nom: string;
  cree_le: string;
}

export interface Paquet {
  id: string;
  parentId: string | null;
  nom: string;
  creeLe: Date;
}

export function paquetDepuisLigne(ligne: LignePaquet): Paquet {
  return {
    id: ligne.id,
    parentId: ligne.parent_id,
    nom: ligne.nom,
    creeLe: new Date(ligne.cree_le),
  };
}

// --- Réglages --------------------------------------------------------------------------

export interface EtapesJson {
  a_revoir: number | null;
  passages: { difficile: number; bien?: number }[];
}

export interface LigneReglages {
  paquet_id: string;
  afficher_fil_ariane: boolean;
  ordre_cartes: OrdreCartes;
  seuil_alerte: number | null;
  nouvelles_cartes_par_jour: number;
  revisions_max_par_jour: number;
  objectif_memorisation: number;
  intervalle_maximum_jours: number;
  etapes_apprentissage: EtapesJson;
  etapes_reapprentissage: EtapesJson;
  optimisation_fsrs: boolean;
  parametres_fsrs: number[] | null;
  heure_debut_journee: number;
  fenetre_debordement_minutes: number;
  retournement_auto_secondes: number | null;
  affichage_minuteur: AffichageMinuteur;
}

export function etapesDepuisJson(json: EtapesJson): Etapes {
  return {
    aRevoir: json.a_revoir,
    passages: json.passages.map((passage) =>
      passage.bien === undefined
        ? { difficile: passage.difficile }
        : { difficile: passage.difficile, bien: passage.bien },
    ),
  };
}

export function etapesVersJson(etapes: Etapes): EtapesJson {
  return {
    a_revoir: etapes.aRevoir,
    passages: etapes.passages.map((passage) =>
      passage.bien === undefined
        ? { difficile: passage.difficile }
        : { difficile: passage.difficile, bien: passage.bien },
    ),
  };
}

export function reglagesDepuisLigne(ligne: LigneReglages): ReglagesPaquet {
  return {
    afficherFilAriane: ligne.afficher_fil_ariane,
    ordreCartes: ligne.ordre_cartes,
    seuilAlerte: ligne.seuil_alerte,
    nouvellesCartesParJour: ligne.nouvelles_cartes_par_jour,
    revisionsMaxParJour: ligne.revisions_max_par_jour,
    objectifMemorisation: ligne.objectif_memorisation,
    intervalleMaximumJours: ligne.intervalle_maximum_jours,
    etapesApprentissage: etapesDepuisJson(ligne.etapes_apprentissage),
    etapesReapprentissage: etapesDepuisJson(ligne.etapes_reapprentissage),
    optimisationFsrs: ligne.optimisation_fsrs,
    parametresFsrs: ligne.parametres_fsrs,
    heureDebutJournee: ligne.heure_debut_journee,
    fenetreDebordementMinutes: ligne.fenetre_debordement_minutes,
    retournementAutoSecondes: ligne.retournement_auto_secondes,
    affichageMinuteur: ligne.affichage_minuteur,
  };
}

const COLONNES_REGLAGES = {
  afficherFilAriane: 'afficher_fil_ariane',
  ordreCartes: 'ordre_cartes',
  seuilAlerte: 'seuil_alerte',
  nouvellesCartesParJour: 'nouvelles_cartes_par_jour',
  revisionsMaxParJour: 'revisions_max_par_jour',
  objectifMemorisation: 'objectif_memorisation',
  intervalleMaximumJours: 'intervalle_maximum_jours',
  etapesApprentissage: 'etapes_apprentissage',
  etapesReapprentissage: 'etapes_reapprentissage',
  optimisationFsrs: 'optimisation_fsrs',
  parametresFsrs: 'parametres_fsrs',
  heureDebutJournee: 'heure_debut_journee',
  fenetreDebordementMinutes: 'fenetre_debordement_minutes',
  retournementAutoSecondes: 'retournement_auto_secondes',
  affichageMinuteur: 'affichage_minuteur',
} as const satisfies Record<keyof ReglagesPaquet, keyof LigneReglages>;

/** Colonnes à mettre à jour pour les réglages modifiés. */
export function ligneDepuisReglages(reglages: Partial<ReglagesPaquet>): Partial<LigneReglages> {
  const ligne: Record<string, unknown> = {};
  for (const [cle, valeur] of Object.entries(reglages) as [keyof ReglagesPaquet, unknown][]) {
    if (valeur === undefined) continue;
    ligne[COLONNES_REGLAGES[cle]] =
      cle === 'etapesApprentissage' || cle === 'etapesReapprentissage'
        ? etapesVersJson(valeur as Etapes)
        : valeur;
  }
  return ligne as Partial<LigneReglages>;
}

// --- État FSRS -------------------------------------------------------------------------

/** État FSRS tel que stocké en JSON dans le journal (dates en texte ISO). */
export interface EtatFsrsJson {
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: number;
  last_review?: string;
}

export function etatFsrsVersJson(etat: EtatFsrs): EtatFsrsJson {
  const json: EtatFsrsJson = {
    due: etat.due.toISOString(),
    stability: etat.stability,
    difficulty: etat.difficulty,
    elapsed_days: etat.elapsed_days,
    scheduled_days: etat.scheduled_days,
    learning_steps: etat.learning_steps,
    reps: etat.reps,
    lapses: etat.lapses,
    state: etat.state,
  };
  if (etat.last_review !== undefined) json.last_review = etat.last_review.toISOString();
  return json;
}

/** Colonnes FSRS d'une carte (ou objet JSON du journal), dates en texte. */
export interface ChampsFsrs {
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: number;
  last_review?: string | null;
}

export function etatFsrsDepuisChamps(champs: ChampsFsrs): EtatFsrs {
  const etat: EtatFsrs = {
    due: new Date(champs.due),
    stability: champs.stability,
    difficulty: champs.difficulty,
    elapsed_days: champs.elapsed_days,
    scheduled_days: champs.scheduled_days,
    learning_steps: champs.learning_steps,
    reps: champs.reps,
    lapses: champs.lapses,
    state: champs.state as State,
  };
  if (champs.last_review !== undefined && champs.last_review !== null) {
    etat.last_review = new Date(champs.last_review);
  }
  return etat;
}

// --- Cartes ----------------------------------------------------------------------------

/** Ligne renvoyée par la fonction cartes_du_paquet. */
export interface LigneCarteDuPaquet extends ChampsFsrs {
  id: string;
  carte_mere_id: string;
  cle: string;
  rang: number;
  suspendue: boolean;
  cree_le: string;
  type: TypeCarte;
  mode: CarteMere['mode'];
  paquet_id: string;
  chemin: string[];
}

/** Carte lue dans la base : ce qu'il faut à la séance, plus de quoi l'afficher. */
export interface CarteLue extends CarteDuPaquet {
  cle: string;
  type: TypeCarte;
  mode: CarteMere['mode'];
  paquetId: string;
  /** Fil d'Ariane, du paquet principal au sous-paquet qui contient la carte. */
  chemin: string[];
}

export function carteDepuisLigne(ligne: LigneCarteDuPaquet): CarteLue {
  return {
    id: ligne.id,
    carteMereId: ligne.carte_mere_id,
    reportFilles: reportAuLendemain({ type: ligne.type, mode: ligne.mode } as CarteMere),
    creeLe: new Date(ligne.cree_le),
    rang: ligne.rang,
    suspendue: ligne.suspendue,
    fsrs: etatFsrsDepuisChamps(ligne),
    cle: ligne.cle,
    type: ligne.type,
    mode: ligne.mode,
    paquetId: ligne.paquet_id,
    chemin: ligne.chemin,
  };
}

export interface LigneCarteMere {
  id: string;
  paquet_id: string;
  type: TypeCarte;
  mode: CarteMere['mode'];
  contenu: unknown;
  cree_le: string;
  modifie_le: string;
}

export interface CarteMereLue {
  id: string;
  paquetId: string;
  carte: CarteMere;
  creeLe: Date;
  modifieLe: Date;
}

export function carteMereDepuisLigne(ligne: LigneCarteMere): CarteMereLue {
  return {
    id: ligne.id,
    paquetId: ligne.paquet_id,
    carte: { type: ligne.type, mode: ligne.mode, contenu: ligne.contenu } as CarteMere,
    creeLe: new Date(ligne.cree_le),
    modifieLe: new Date(ligne.modifie_le),
  };
}

// --- Révisions -------------------------------------------------------------------------

/** Ligne renvoyée par la fonction revisions_du_paquet. */
export interface LigneRevision {
  id: number;
  carte_id: string;
  revise_le: string;
  note: Note;
  etat_avant: number;
  temps_reponse_ms: number;
}

export interface RevisionLue extends RevisionPassee {
  id: number;
  note: Note;
  tempsReponseMs: number;
}

export function revisionDepuisLigne(ligne: LigneRevision): RevisionLue {
  return {
    id: ligne.id,
    carteId: ligne.carte_id,
    reviseLe: new Date(ligne.revise_le),
    note: ligne.note,
    etatAvant: ligne.etat_avant as State,
    tempsReponseMs: ligne.temps_reponse_ms,
  };
}

// --- Préférences -----------------------------------------------------------------------

export interface LignePreferences {
  touches: Touches;
  derniers_modes: { [T in TypeCarte]: Extract<CarteMere, { type: T }>['mode'] };
  couleurs_surlignage: string[];
}

export interface Preferences {
  touches: Touches;
  derniersModes: LignePreferences['derniers_modes'];
  couleursSurlignage: string[];
}

export function preferencesDepuisLigne(ligne: LignePreferences): Preferences {
  return {
    touches: ligne.touches,
    derniersModes: ligne.derniers_modes,
    couleursSurlignage: ligne.couleurs_surlignage,
  };
}
